import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Shield, Settings, LayoutDashboard, MessageSquare, Info,
  Server, Scale, TrendingUp, Lock, ShieldCheck,
  DollarSign, Package, Brain, ClipboardList, Globe, Star, Award
} from 'lucide-react';

const adminCards = [
  {
    title: 'Overview',
    description: 'System health, users, revenue, valuation, activity, and team (read-only)',
    icon: LayoutDashboard,
    accentClass: 'text-blue-500',
    borderClass: 'border-blue-500/30',
    path: '/app/admin/overview'
  },
  {
    title: 'App Settings Home',
    description: 'App Profile, Platform Identity, Pricing, Storage, Self-Marketing, System Control',
    icon: Settings,
    accentClass: 'text-purple-500',
    borderClass: 'border-purple-500/30',
    path: '/app/admin/app-settings'
  },
  {
    title: 'Infrastructure Monitor',
    description: 'System health, uptime, and infrastructure status',
    icon: Server,
    accentClass: 'text-cyan-500',
    borderClass: 'border-cyan-500/30',
    path: '/app/admin/infrastructure'
  },
  {
    title: 'Legal Intelligence System',
    description: 'Terms, privacy, compliance docs, and legal document lifecycle',
    icon: Scale,
    accentClass: 'text-red-500',
    borderClass: 'border-red-500/30',
    path: '/internal/legal'
  },
  {
    title: 'Growth Engine',
    description: 'Flywheel metrics, growth loops, and acquisition performance',
    icon: TrendingUp,
    accentClass: 'text-green-500',
    borderClass: 'border-green-500/30',
    path: '/app/admin/growth'
  },
  {
    title: 'Permissions & Access Control',
    description: 'Admin access, roles, and team permission management',
    icon: Lock,
    accentClass: 'text-indigo-500',
    borderClass: 'border-indigo-500/30',
    path: '/app/admin/team-permissions'
  },
  {
    title: 'Support & Knowledge System',
    description: 'Support overview, ticket management, Help Center articles',
    icon: MessageSquare,
    accentClass: 'text-yellow-500',
    borderClass: 'border-yellow-500/30',
    path: '/app/admin/support'
  },
  {
    title: 'Moderation System',
    description: 'Content moderation, flagging, and community safety',
    icon: ShieldCheck,
    accentClass: 'text-orange-500',
    borderClass: 'border-orange-500/30',
    path: '/app/admin/moderation'
  },
  {
    title: 'Financial Control',
    description: 'Revenue tracking, payouts, and financial oversight',
    icon: DollarSign,
    accentClass: 'text-emerald-500',
    borderClass: 'border-emerald-500/30',
    path: '/app/admin/financial-control'
  },
  {
    title: 'Fulfillment System',
    description: 'Order fulfillment, delivery tracking, and service completion',
    icon: Package,
    accentClass: 'text-blue-400',
    borderClass: 'border-blue-400/30',
    path: '/app/admin/fulfillment'
  },
  {
    title: 'System Intelligence',
    description: 'AI-driven system insights and platform intelligence',
    icon: Brain,
    accentClass: 'text-violet-500',
    borderClass: 'border-violet-500/30',
    path: '/app/admin/system-intelligence'
  },
  {
    title: 'Admin Task Engine',
    description: 'Admin tasks, operations queue, and workflow management',
    icon: ClipboardList,
    accentClass: 'text-amber-500',
    borderClass: 'border-amber-500/30',
    path: '/app/admin/task-engine'
  },
  {
    title: 'Public Pages System',
    description: 'Public-facing pages, landing pages, and SEO management',
    icon: Globe,
    accentClass: 'text-sky-500',
    borderClass: 'border-sky-500/30',
    path: '/app/admin/public-pages'
  },
  {
    title: 'Creator Spotlight System',
    description: 'Featured creators, highlights, and community showcases',
    icon: Star,
    accentClass: 'text-yellow-400',
    borderClass: 'border-yellow-400/30',
    path: '/app/admin/creator-spotlight'
  },
  {
    title: 'Reward System',
    description: 'Reward programs, incentive management, and distribution',
    icon: Award,
    accentClass: 'text-pink-500',
    borderClass: 'border-pink-500/30',
    path: '/app/admin/reward-system'
  }
];

export default function AdminDashboard() {
  const navigate = useNavigate();

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-2">
          <Shield className="h-8 w-8 text-pink-500" />
          ADMIN
        </h1>
        <p className="text-[#8A93A6]">Read-only governance & platform management</p>
      </div>

      <Alert className="bg-blue-500/10 border-blue-500/30 mb-6">
        <Info className="h-4 w-4 text-blue-500" />
        <AlertDescription className="text-blue-400 text-sm">
          <strong>Admin is observation + governance only.</strong> No execution buttons. Overview cards drill down to deeper Admin pages.
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {adminCards.map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.title} onClick={() => navigate(card.path)} className="cursor-pointer">
              <Card className={`glass hover:bg-white/5 transition-all h-full ${card.borderClass}`}>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Icon className={`h-5 w-5 ${card.accentClass}`} />
                    {card.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-[#8A93A6]">{card.description}</p>
                </CardContent>
              </Card>
            </div>
          );
        })}
      </div>
    </div>
  );
}
